import type {
  LaunchRequest,
  LaunchResponse,
  OpenRoundRequest,
  OpenRoundResponse,
  ResolveRoundRequest,
  ResolveRoundResponse,
} from "@vb/rgs-contract";
import { configForCurrency } from "@vb/rgs-contract";

// =============================================================================
// Demo RGS client — DEV-ONLY fallback for when the real game-server is
// unreachable (ECONNREFUSED in local dev). Mirrors the public surface of
// `rgs-client.ts` with synthetic fixtures so the UI hydrates and the new
// design is visible without needing a backend.
//
// Round mechanics are intentionally minimal: PLACE BET succeeds, multiplier
// stays at 1.0× (no WS tick stream is faked), and CASH OUT picks a random
// outcome. The intent is dev visibility, not playable simulation.
// =============================================================================

// Currency-aware starting balance — set on first launch from the request.
// Without this, a `?currency=USDT` demo would hand out 0.05 USDT (~$0.05)
// and be unplayable in the demo client.
let balance = 0.05;
let nonceCounter = 1;

function rid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function syntheticHash(): string {
  const chars = "0123456789abcdef";
  let out = "";
  for (let i = 0; i < 64; i++) out += chars[Math.floor(Math.random() * 16)];
  return out;
}

async function delay<T>(value: T, ms = 60): Promise<T> {
  return new Promise((res) => setTimeout(() => res(value), ms));
}

export const demoRgs = {
  async launch(body: LaunchRequest): Promise<LaunchResponse> {
    // Honor the requested currency + reset the demo balance to the
    // currency's natural default (BTC=0.05, USDT=5000, etc.) so a USDT
    // demo isn't priced like a BTC one.
    const cfg = configForCurrency(body.currency);
    balance = cfg.defaultStartingBalance;
    return delay({
      sessionId: rid("demo-sess"),
      token: rid("demo-tok"),
      currency: body.currency,
      // Echo the requested gameSlug so the store hydrates with the right
      // game binding even when the real game-server is unreachable.
      gameSlug: body.gameSlug,
      balance,
      serverSeedHash: syntheticHash(),
      clientSeed: rid("demo-cs"),
    });
  },

  async open(body: OpenRoundRequest): Promise<OpenRoundResponse> {
    balance = Math.max(0, balance - body.betAmount);
    return delay({
      roundId: rid("demo-round"),
      nonce: nonceCounter++,
      balance,
      clientSeed: rid("demo-cs"),
      serverSeedHash: syntheticHash(),
    });
  },

  async resolve(body: ResolveRoundRequest): Promise<ResolveRoundResponse> {
    // ResolveRoundRequest is a discriminated union now. Extract the cashout
    // multiplier per-game; PP/CC don't carry one (their payout is derived
    // from tilesRevealed / guesses respectively), but the demo stub just
    // simulates a coin-flip win for any game so the UI keeps cycling.
    const requestedMult =
      body.gameSlug === "vault-breaker" ? body.cashoutMultiplier : 1;
    // 55% win, payout at the requested multiplier (capped at 50× for sanity).
    const win = Math.random() < 0.55;
    const cashoutMultiplier = win ? Math.min(50, requestedMult) : 0;
    const payout = win ? requestedMult : 0;
    if (win) balance += payout * 0.001;
    return delay({
      roundId: body.roundId,
      outcome: win ? "win" : "bust",
      cashoutMultiplier,
      payout,
      balance,
    });
  },
};
